import { createUseStyles } from 'react-jss';
import { ButtonPlacements, CarouselVariants } from '../types/index.js';
import { PrimaryColors } from '../../../../foundation/colors/primaryColors/primaryColors/index.js';
import { Timings } from '../../../../foundation/timings/timings/index.js';
import { Spacings } from '../../../../foundation/spacings/spacings/index.js';
import { ControlBorderColors } from '../../../../foundation/colors/controlBorderColors/controlBorderColors/index.js';
import { CornerRadius } from '../../../../foundation/cornerRadius/cornerRadius/index.js';
import { Easing } from '../../../../foundation/easing/easing/index.js';
import { canTouch, canHover } from '../../../../foundation/styleHelpers/hoverTouch/hoverTouch/index.js';
import { Shadows } from '../../../../foundation/colors/shadows/shadows/index.js';
import { BackgroundColors } from '../../../../foundation/colors/backgroundColors/backgroundColors/index.js';
import { ControlStartIconColors } from '../../../../foundation/colors/controlStartIconColors/controlStartIconColors/index.js';
import { BreakPointsMedia } from '../../../../foundation/responsive/responsive/index.js';
import { isRightToLeft } from '../../../../foundation/styleHelpers/rtl/rtl/index.js';

const hoverPagination = {
    '&:hover': {
        backgroundColor: PrimaryColors.base.action,
    },
};
const paginationStyleOnContent = {
    position: 'absolute',
    transform: 'translate(-50%)',
    left: '50%',
    bottom: Spacings.s32,
};
const paginationStyleBellowContent = {
    marginTop: Spacings.s16,
    marginBottom: Spacings.s16,
};
const overhangArrow = { position: 'absolute' };
const DOTE_SIZE = 8;
const usePaginationStyles = createUseStyles({
    pagination: {
        display: 'flex',
        justifyContent: 'center',
        listStyle: 'none',
        extend: ({ dotsPlacement }) => (dotsPlacement === ButtonPlacements.Overlay ? { ...paginationStyleOnContent } : { ...paginationStyleBellowContent }),
    },
    scroller: {
        display: 'flex',
        transition: `transform ${Timings.default} ${Easing.easeOut}`,
        willChange: 'transform',
    },
    paginationItem: {
        width: DOTE_SIZE,
        height: DOTE_SIZE,
        borderRadius: CornerRadius.circle,
        backgroundColor: ControlBorderColors.default,
        margin: [Spacings.s0, Spacings.s4],
        flexShrink: 0,
        transition: `transform ${Timings.default}, opacity ${Timings.default}, background-color ${Timings.default}`,
        cursor: 'pointer',
        opacity: 0,
        transform: 'scale(0)',
        [canHover]: hoverPagination,
        [canTouch]: hoverPagination,
    },
    active: {
        backgroundColor: PrimaryColors.base.default,
    },
    visible: {
        opacity: 1,
        transform: 'scale(0.6)',
    },
    midVisible: {
        opacity: 1,
        transform: 'scale(1)',
    },
    normal: {
        opacity: 1,
        transform: 'scale(1)',
    },
    edgeDot: {
        transform: 'scale(0.6)',
        opacity: 1,
    },
});
const useStyles = createUseStyles({
    root: {
        position: 'relative',
        width: '100%',
        userSelect: 'none',
        height: ({ sizes }) => sizes.Default.value,
        [BreakPointsMedia.XXL]: {
            height: ({ sizes }) => sizes.XXL.value,
        },
        [BreakPointsMedia.XL]: {
            height: ({ sizes }) => sizes.XL.value,
        },
        [BreakPointsMedia.LG]: {
            height: ({ sizes }) => sizes.LG.value,
        },
        [BreakPointsMedia.MD]: {
            height: ({ sizes }) => sizes.MD.value,
        },
        [BreakPointsMedia.SM]: {
            height: ({ sizes }) => sizes.SM.value,
        },
        [BreakPointsMedia.XS]: {
            height: ({ sizes }) => sizes.XS.value,
        },
        [BreakPointsMedia.XXS]: {
            height: ({ sizes }) => sizes.XXS.value,
        },
    },
    contentWrapper: {
        overflow: 'hidden',
    },
    carouselContent: {
        display: 'flex',
        justifyContent: 'start',
        width: '100%',
        overscrollBehaviorX: 'contain',
        '&::-webkit-scrollbar': {
            display: 'none',
        },
        '-ms-overflow-style': 'none',
        'scrollbar-width': 'none',
        transition: ({ isFirstRender, variant }) => (!isFirstRender && variant === CarouselVariants.Swipe && `transform ${Timings.default} ${Easing.easeOut}`),
        overflowX: ({ isMultiSlide }) => isMultiSlide && 'auto',
        scrollSnapType: ({ isMultiSlide }) => isMultiSlide && 'x mandatory',
        scrollBehavior: ({ isMultiSlide }) => isMultiSlide && 'smooth',
    },
    slide: {
        display: 'flex',
        justifyContent: 'center',
        flexShrink: 0,
        margin: '0 auto',
        width: '100%',
        transform: 'translate3d(0, 0, 0)',
        opacity: ({ variant }) => variant === CarouselVariants.FadeInOut && 0,
        transition: ({ variant }) => variant === CarouselVariants.FadeInOut && `opacity ${Timings.default} ${Easing.easeOut}`,
    },
    active: {
        opacity: ({ variant }) => variant === CarouselVariants.FadeInOut && 1,
    },
    icon: {
        '& path': {
            fill: ControlStartIconColors.default,
        },
    },
    arrowButtonWrap: {
        display: 'flex',
        alignItems: 'center',
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        width: 60,
        height: '100%',
        pointerEvents: 'none',
        '&.left': {
            insetInlineStart: Spacings.s0,
            background: ({ buttonPlacement, showGradients }) => (buttonPlacement === ButtonPlacements.Overlay && showGradients && BackgroundColors.gradientLeft),
            [isRightToLeft]: {
                background: ({ buttonPlacement, showGradients }) => (buttonPlacement === ButtonPlacements.Overlay && showGradients && BackgroundColors.gradientLeft),
            },
        },
        '&.right': {
            insetInlineEnd: Spacings.s0,
            background: ({ buttonPlacement, showGradients }) => (buttonPlacement === ButtonPlacements.Overlay && showGradients && BackgroundColors.gradientRight),
            [isRightToLeft]: {
                background: ({ buttonPlacement, showGradients }) => (buttonPlacement === ButtonPlacements.Overlay && showGradients && BackgroundColors.gradientRight),
            },
        },
    },
    arrowButton: {
        extend: ({ buttonPlacement }) => buttonPlacement === ButtonPlacements.Overhang && overhangArrow,
        boxShadow: Shadows.tint1,
        pointerEvents: 'auto',
        '&.left': {
            left: ({ buttonPlacement }) => (buttonPlacement === ButtonPlacements.Overhang ? '-40%' : null),
            marginInlineStart: ({ buttonPlacement }) => (buttonPlacement === ButtonPlacements.Overlay ? Spacings.s12 : Spacings.s0),
        },
        '&.right': {
            right: ({ buttonPlacement }) => (buttonPlacement === ButtonPlacements.Overhang ? '-40%' : null),
            marginInlineEnd: ({ buttonPlacement }) => (buttonPlacement === ButtonPlacements.Overlay && Spacings.s12),
        },
        [isRightToLeft]: {
            transform: ({ isMultiSlide }) => isMultiSlide && 'scaleX(-1)',
        },
    },
    mergedSlide: ({ gaps }) => ({
        display: 'inline-flex',
        gap: gaps.Default.value,
        [BreakPointsMedia.XXL]: {
            gap: gaps.XXL.value,
        },
        [BreakPointsMedia.XL]: {
            gap: gaps.XL.value,
        },
        [BreakPointsMedia.LG]: {
            gap: gaps.LG.value,
        },
        [BreakPointsMedia.MD]: {
            gap: gaps.MD.value,
        },
        [BreakPointsMedia.SM]: {
            gap: gaps.SM.value,
        },
        [BreakPointsMedia.XS]: {
            gap: gaps.XS.value,
        },
        [BreakPointsMedia.XXS]: {
            gap: gaps.XXS.value,
        },
    }),
});

export { usePaginationStyles, useStyles };
